import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Button, Divider, IconButton, Modal, Tooltip, Typography } from '@mui/material';
import { AppConfig } from 'AppConfig';
import SmartCard from 'common/bstCard/smartCard';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hasPermission } from 'utils/common';
import { store } from 'store/store';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Actions from './actions/actions';
import { fetchFeatureReports } from './defaultSetting/actions/action';
import NoAvailableReports from 'features/frontoffice/component/noAvailableReports';
import axios from 'axios';
import { FoMaterialTable } from 'common/FoMaterialTable';
import HideSmartCardDialog from 'features/landing/newHome/HideSmartCardDialog';
import { sortReports } from 'utils/reportSort';
import { IconArrowNarrowRight } from '@tabler/icons-react';
import SmartCardsLoading from './component/smartCardsLoading';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { HIDE_SMART_CARD_REQUEST } from './actions/types';
import { showRestoreToast } from 'utils/showRestoreToast';
import { buildMetricsFilters, getFilterHash } from 'features/metrics/metricsActions';
import { useSmartCardVdsTrigger } from 'features/frontoffice/useSmartCardVdsTrigger';

const isAvailable = (arr) => {
  return arr && Array.isArray(arr) && arr.length > 0;
};

const getSmartCardKey = (smart, index) => smart?.id || smart?.scId || smart?.displayText || index;

const SmartCards = ({sortBy,searchTerm = '',embedded = false, viewMode = 'grid'}) => {
  // AGENT-CONTEXT: this component is also mounted directly by its own route, outside
  // any ancestor that triggers the fetch, and it gates its skeleton on the settled
  // sc-vds filter hash. Without this trigger a direct load never settles. The action
  // dedups in-flight/cached requests, so the redundant call from an embedding parent
  // is harmless. Override only if a common route ancestor owns the trigger instead.
  useSmartCardVdsTrigger();
  const isMobil = useMediaQuery({ query: '(min-width: 320px) and (max-width: 767px)' });
  const isTablet = useMediaQuery({ query: '(min-width: 768px) and (max-width: 1279px)' });
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const dispatch = useDispatch();

  const currentApp = useSelector((state) => state.user.currentApp);
  const smarts = useSelector((state) => state.fo.smarts);
  const metrics = useSelector((state) => state.fo.metrics);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);
  const vdsLoadingFlag = useSelector((state) => state.fo.smartCardVdsLoading);
  const vdsSettledFilterHash = useSelector((state) => state.fo.smartCardVdsFilterHash);
  const appliedFilters = useSelector((state) => state.globalFilters?.applied);
  const defaultsSeeded = useSelector((state) => state.globalFilters?.defaultsSeeded);
  const pendingHiddenSmartCardIds = useSelector((state) => state.fo.pendingHiddenSmartCardIds);
  const isOperationInProgress = pendingHiddenSmartCardIds.length > 0;
  const showHiddenSmartsLink = useSelector((state) => state.fo.showHiddenSmartsLink);
  const showDismiss = hasPermission(AppConfig.PERMISSION.FO_SMARTCARD_DISMISS, permissions);
  const showRecover = hasPermission(AppConfig.PERMISSION.FO_SMARTCARD_RECOVER, permissions);
  const [showSmartCard, setShowSmartCard] = useState(false);
  const [selectedSmartCardId, setSelectedSmartCardId] = useState({ id: null, title: '' });
  const sortKey = typeof sortBy === 'string' ? sortBy : sortBy?.value;
  const displaySmarts = useMemo(() => {
   const base = smarts?.filter((s) => s.category === AppConfig.REPORT_CATEGORY.SMART.code && s.dismissed !== true) || [];
   if(sortKey === 'recently_saved') {
    return base.filter((s) => s.favorited === true);
   }
   return base;
  },[smarts, sortKey]);
  const hiddenSmarts = smarts?.filter((s) => s.category === AppConfig.REPORT_CATEGORY.SMART.code && s.dismissed === true) || [];
  
  const [isTenantLoading, setIsTenantLoading] = useState(false);
  // Mirror the /bi metric cards: while /tbl/sc-vds is loading for the applied
  // filter context, keep the skeleton up instead of showing stale values.
  const appliedFilterHash = useMemo(
    () => getFilterHash(buildMetricsFilters(appliedFilters)),
    [appliedFilters]
  );

  const isKpiLoading = !defaultsSeeded || vdsLoadingFlag || appliedFilterHash !== vdsSettledFilterHash;
  const isLoading = isTenantLoading || smarts === null || smarts === undefined;
  const isLinkNewlyAppearing = hiddenSmarts?.length <= 1;
  const shouldLockLink = isOperationInProgress && isLinkNewlyAppearing;
  const showVisibleSmartCardsHeading = isLoading || !!smarts?.length || showHiddenSmartsLink;

  const navigate = useNavigate();

  const sortedSmarts = useMemo(() => {
    return sortReports(displaySmarts, sortKey, { smart: true });
  }, [displaySmarts, sortKey]);

  const searchedSmarts = useMemo(() => {
    const list = Array.isArray(sortedSmarts) ? sortedSmarts : [];
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) => {
      const title = (item?.displayText || '').toLowerCase();
      const desc = (item?.description || '').toLowerCase();
      return title.includes(term) || desc.includes(term);
    });
  }, [sortedSmarts, searchTerm]);

  const placeholderCards = useMemo(() => Array.from({ length: 6 }, (_, index) => ({id: `placeholder-${index}`, isPlaceholder: true})), []);
  const showPlaceholders = isLoading;
  const gridSmarts = showPlaceholders ? placeholderCards : searchedSmarts;
  const showNoSmartCards = !isLoading && searchedSmarts.length === 0;

  const smartCardsGridKey = useMemo(
    () => [sortKey || 'default', ...searchedSmarts.map((smart, index) => getSmartCardKey(smart, index))].join('|'),
    [searchedSmarts, sortKey]
  );

  const [, setSelectedMetrics] = useState(new Set());
  const [, setMetrixPanelClosed] = useState(true);

  /* Recovery dialog state */
  const [chartRecoveryModal, setChartRecoveryModal] = useState(false);
  const [recoverableCharts, setRecoverableCharts] = useState([]);
  const [selectedRecoverableCharts, setSelectedRecoverableCharts] = useState([]);

  useEffect(() => {
    if (!embedded) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const refresh = useCallback(() => {
    const { smarts: cachedSmarts, smartsTenantCd } = store.getState().fo;
    if (cachedSmarts == null || smartsTenantCd !== currentTenantCd) {
      setIsTenantLoading(true);
    }
    store.dispatch(Actions.getFoDetail(currentTenantCd));
    store.dispatch(Actions.fetchMetricsByTenant());
    store.dispatch(fetchFeatureReports());
  }, [currentTenantCd]);

  useEffect(() => {
    if (smarts !== null && smarts !== undefined) {
      setIsTenantLoading(false);
    }
  }, [smarts]);


  /**
   * Shows recoverable smart cards
   */
  const showSmartRecoverableCharts = () => {
    axios
      .post(`${AppConfig.FO_INITIAL_URL}/init/getmuted`, { category: AppConfig.REPORT_CATEGORY.SMART.code })
      .then((resp) => {
        setRecoverableCharts(resp.data);
        setChartRecoveryModal(true);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * Recovers selected smart cards
   */
  const recoverCharts = () => {
    axios
      .post(`${AppConfig.FO_INITIAL_URL}/init/recovercharts`, selectedRecoverableCharts)
      .then(() => {
        setChartRecoveryModal(false);
        setRecoverableCharts([]);
        setSelectedRecoverableCharts([]);
        // Refresh data without page reload
        refresh();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  /**
   * Cancels recovery dialog
   */
  const onRecoveryCancel = () => {
    setChartRecoveryModal(false);
    setRecoverableCharts([]);
    setSelectedRecoverableCharts([]);
  };

  useEffect(() => {
    if (isAvailable(metrics)) {
      const newSelected = new Set();
      metrics.forEach((m) => {
        newSelected.add(m.displayText);
      });
      setMetrixPanelClosed(true); // Work around for bug BSTP-1267
      setSelectedMetrics(newSelected);
    } else {
      setSelectedMetrics(new Set());
      setMetrixPanelClosed(true);
    }
  }, [metrics]);

  const handleHideSmartCard = () => {
    if (!selectedSmartCardId) return;
    dispatch({ type: HIDE_SMART_CARD_REQUEST });
    store.dispatch(Actions.dismissReport(selectedSmartCardId?.id)).catch((error) => {
      showRestoreToast("Failed to hide smartcard. Please try again." , "580px" , "300px" , "#88041E" , true);
    });
    setShowSmartCard(false);
    setSelectedSmartCardId({ id: null, title: '' });
  };

  useEffect(() => {
    if (currentApp === AppConfig.APP_CODE_FO) {
      refresh();
    }
  }, [currentApp, refresh]);

  // Refresh when navigating to Smart Cards page to sync favorites from Home
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = prevPathnameRef.current;

    // Only trigger if we navigated FROM Home page TO Smart Cards page
    if ((previousPath === '/frontoffice' || previousPath === '/frontoffice/') && currentPath.includes('/frontoffice/smartcards')) {
      refresh();
    }

    // Update previous pathname
    prevPathnameRef.current = currentPath;
  }, [location.pathname, refresh]);

  return (
    <Box sx={{ bgcolor: '#FFFFFF', width: '100%' }}>
      {isMobil ? (
        <Box sx={{ boxSizing: 'border-box', overflowX: 'hidden', mx: '-18px', pt: 3, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
          <Box sx={{ width: '100%', boxSizing: 'border-box', px: '24px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Box sx={{ alignSelf: 'flex-start', mt: 2, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {showVisibleSmartCardsHeading && (
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Rubik',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '150%',
                    letterSpacing: '0.16px'
                  }}>
                  Visible ({isLoading ? '...' : searchedSmarts.length})
                </Typography>
              )}
              {(!isLoading && !!searchedSmarts?.length) && (
                <Typography
                  sx={{
                    color: '#121A22',
                    fontFamily: 'Noto Sans',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '150%'
                  }}>
                  {"Showing smart cards you've chosen to keep visible."}
                </Typography>
              )}
              {(hiddenSmarts?.length > 0 || showHiddenSmartsLink) && (
                <Link
                  component={RouterLink}
                  to="/bi/hidden-smart-card"
                  underline="none"
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Noto Sans',
                    fontWeight: 600,
                    color: '#2F54A5',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    lineHeight: '150%',
                    '&:hover': { color: '#068DF1', textDecoration: 'none', background: 'transparent' }
                  }}>
                  View Hidden Smart Cards
                  <IconArrowNarrowRight size={16} />
                </Link>
              )}
            </Box>

          <Box sx={{ display: 'flex', width: '100%', flexWrap: 'wrap', gap: '16px', mt: '16px', justifyContent: 'center' }}>
              {isLoading ? (
                <SmartCardsLoading count={3} variant="stack" />
              ) : showNoSmartCards ? (
                  <NoAvailableReports isSmartCards />
              ) : (
                searchedSmarts.map((smart, idx) => (
                  <Box
                    key={getSmartCardKey(smart, idx)}
                    sx={{ width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', mb: '16px' }}>
                    <SmartCard
                      item={smart}
                      reportType={AppConfig.SMART_CAROUSEL}
                      showDismiss={showDismiss}
                      permissions={permissions}
                      setShowSmartCard={setShowSmartCard}
                      setSelectedSmartCardId={setSelectedSmartCardId}
                    />
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      ) : isTablet ? (
        <Box sx={{ width: '100%', mx: 'auto', pt: 3 }}>
          <Box sx={{ display: 'flex', width: '100%', flexWrap: 'wrap', gap: '24px', justifyContent: 'left' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                mb: 1,
              }}
            >
              <Box>
                {showVisibleSmartCardsHeading && (
                  <Typography
                    sx={{
                      color: '#000',
                      fontFamily: 'Rubik',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '150%',
                      letterSpacing: '0.16px',
                      margin: 0,
                      paddingBottom: '8px',
                    }}
                  >
                    {`Visible (${isLoading ? '...' : searchedSmarts.length})`}
                  </Typography>
                )}
                {(!isLoading && !!searchedSmarts?.length) && (
                  <Typography
                    sx={{
                      color: '#121A22',
                      fontFamily: 'Noto Sans',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '150%',
                      margin: 0,
                    }}>
                    {"Showing smart cards you've chosen to keep visible."}
                  </Typography>
                )}
              </Box>
              {(hiddenSmarts?.length > 0 || showHiddenSmartsLink) && (
                <Link
                  component={RouterLink}
                  to="/bi/hidden-smart-card"
                  underline="none"
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Noto Sans',
                    fontWeight: 600,
                    color: '#2F54A5',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    lineHeight: '150%',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: '#068DF1',
                      textDecoration: 'none',
                    },
                  }}
                >
                  View Hidden Smart Cards
                  <IconArrowNarrowRight size={16} />
                </Link>
              )}
            </Box>
            {isLoading ? (
              <SmartCardsLoading count={4} variant="grid" />
            ) : showNoSmartCards ? (
                 <NoAvailableReports isSmartCards />
            ) : (
              searchedSmarts.map((smart, idx) => (
                <Box
                  key={getSmartCardKey(smart, idx)}
                  sx={{
                    flex: '0 1 calc(50% - 12px)',
                    maxWidth: 'calc(50% - 12px)',
                    minWidth: '300px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                  <SmartCard
                    item={smart}
                    reportType={AppConfig.SMART_CAROUSEL}
                    showDismiss={showDismiss}
                    permissions={permissions}
                    setShowSmartCard={setShowSmartCard}
                    setSelectedSmartCardId={setSelectedSmartCardId}
                  />
                </Box>
              ))
            )}
          </Box>
        </Box>
      ) : embedded ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '24px',
            width: '100%'
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              {showVisibleSmartCardsHeading && (
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Rubik',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '150%',
                    margin: 0,
                    letterSpacing: '0.16px'
                  }}>
                  {`Visible (${isLoading ? '...' : searchedSmarts.length})`}
                </Typography>
              )}

              {(hiddenSmarts?.length > 0 || showHiddenSmartsLink) && (
                <Tooltip title={shouldLockLink ? 'Operation in progress, please wait a few seconds before the link is enabled.' : ''}>
                  <span>
                    <Link
                      component={RouterLink}
                      {...(!shouldLockLink && { to: '/bi/hidden-smart-card' })}
                      underline="none"
                      tabIndex={shouldLockLink ? -1 : 0}
                      aria-disabled={shouldLockLink}
                      onClick={(e) => {
                        if (shouldLockLink) {
                          e.preventDefault();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (shouldLockLink && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                        }
                      }}
                      sx={{
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontFamily: 'Noto Sans',
                        fontWeight: 600,
                        color: '#2F54A5',
                        display: 'inline-flex',
                        alignItems: 'center',
                        lineHeight: '150%',
                        letterSpacing: '0%',
                        gap: 0.5,
                        '&:hover': { color: '#068DF1', textDecoration: 'none', background: 'transparent' },
                        whiteSpace: 'nowrap',
                        cursor: shouldLockLink ? 'not-allowed' : 'pointer',
                        opacity: shouldLockLink ? 0.5 : 1,
                        pointerEvents: shouldLockLink ? 'none' : 'auto'
                      }}>
                        View Hidden Smart Cards
                      <IconArrowNarrowRight size={16} />
                    </Link>
                  </span>
                </Tooltip>
              )}
            </Box>

            {(!isLoading && !!searchedSmarts?.length) && (
              <Box>
                <Typography
                  sx={{
                    color: '#121A22',
                    fontFamily: "Noto Sans",
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '150%',
                    margin: 0
                  }}>
                  {'Showing smart cards you\'ve chosen to keep visible.'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Card grid */}
          <Box sx={{ width: '100%' }}>
            {!isLoading && showNoSmartCards ? (
              <NoAvailableReports isSmartCards />
            ) : (
              <Box
                key={smartCardsGridKey}
                sx={{
                display: viewMode === 'list' ? 'flex' : 'inline-grid',
                flexDirection: viewMode === 'list' ? 'column' : undefined,
                width: '100%',
                rowGap: viewMode === 'list' ? '16px' : '24px',
                columnGap: '24px',
                alignSelf: 'stretch',
                gridTemplateRows: viewMode === 'list' ? undefined : 'repeat(4, fit-content(100%))',
                gridTemplateColumns: viewMode === 'list' ? undefined : { md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
              }}>
                {gridSmarts.map((smart, idx) => (
                  <Box
                    key={getSmartCardKey(smart, idx)}
                    sx={{
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%'
                    }}>
                   <SmartCard
                      item={smart}
                      reportType={AppConfig.SMART_CAROUSEL}
                      showDismiss={showDismiss}
                      permissions={permissions}
                      setShowSmartCard={setShowSmartCard}
                      kpiLoading={isKpiLoading}
                      setSelectedSmartCardId={setSelectedSmartCardId}
                      viewMode={viewMode}
                     />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ width: '100%', maxWidth: 1328, px: '24px', mx: 'auto', pt: 3 }}>
          {(isLoading || searchedSmarts.length > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h1" sx={{ fontFamily: 'Rubik', fontSize: '1.75rem', fontWeight: '500' }}>
                Smart Cards
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 3 }}>
            {!isLoading && showNoSmartCards ? (
              <NoAvailableReports isSmartCards />
            ) : (
              <Box
                key={smartCardsGridKey}
                sx={{
                display: 'inline-grid',
                width: '100%',
                rowGap: '24px',
                columnGap: '24px',
                alignSelf: 'stretch',
                gridTemplateRows: 'repeat(4, fit-content(100%))',
                gridTemplateColumns: { md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }
              }}>
                {gridSmarts.map((smart, idx) => (
                  <Box
                    key={getSmartCardKey(smart, idx)}
                    sx={{
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%'
                    }}>
                     <SmartCard
                      item={smart}
                      reportType={AppConfig.SMART_CAROUSEL}
                      showDismiss={showDismiss}
                      permissions={permissions}
                      setShowSmartCard={setShowSmartCard}
                      setSelectedSmartCardId={setSelectedSmartCardId}
                      viewMode={viewMode}
                      kpiLoading={isKpiLoading}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Recovery Modal */}
      <Modal open={chartRecoveryModal} onClose={onRecoveryCancel}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '24px',
            border: '1px solid #959696',
            py: '40px',
            px: {
              xs: '16px',
              sm: '32px',
              md: '40px'
            },
            width: {
              xs: '95%',
              sm: '90%',
              md: '800px'
            },
            overflowY: 'visible',
            background: '#FFF'
          }}>
          {/* Title and Close button row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: '500', fontFamily: 'Rubik', fontSize: '20px', color: '#121A22', lineHeight: '30px' }}>
              Recover Smart Cards
            </Typography>
            <IconButton onClick={onRecoveryCancel} size="large" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography sx={{ mb: 3, fontSize: '16px', color: '#121A22', fontFamily: 'Rubik', fontWeight: '400' }}>
            Select the smart cards you would like to recover and display again on your page.
          </Typography>
          <Box sx={{ maxHeight: '350px', overflowY: 'auto' }}>
            <FoMaterialTable
              columns={[
                { title: 'SMART CARD', field: 'name' },
                { title: 'ID', field: 'id', hidden: true }
              ]}
              data={recoverableCharts}
              options={{
                selection: true,
                search: false,
                paging: false,
                toolbar: false
              }}
              components={{ OverlayLoading: () => <div /> }}
              onSelectionChange={(rows) => {
                const ids = rows.map((row) => row.id);
                setSelectedRecoverableCharts(ids);
              }}
            />
          </Box>
          <Divider sx={{ width: '100%', mt: '40px', mb: '24px' }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '24px',
              flexDirection: {
                xs: 'column-reverse',
                sm: 'row'
              }
            }}>
            <Button
              variant="outlined"
              onClick={onRecoveryCancel}
              sx={{
                borderRadius: '24px',
                width: {
                  xs: '100%',
                  sm: '188px'
                },
                height: '48px',
                px: 3
              }}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={recoverCharts}
              disabled={selectedRecoverableCharts.length === 0}
              sx={{
                borderRadius: '24px',
                width: {
                  xs: '100%',
                  sm: '188px'
                },
                height: '48px',
                px: 3,
                borderColor: '#2F54A5',
                color: '#2F54A5',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#2F54A5'
                }
              }}>
              Recover
            </Button>
          </Box>
        </Box>
      </Modal>
     {showSmartCard && (
        <HideSmartCardDialog
          open={showSmartCard}
          onClose={() => setShowSmartCard(false)}
          handleHideSmartCard={handleHideSmartCard}
          selectedSmartCardId={selectedSmartCardId}
        />
      )}
    </Box>
  );
};

export default SmartCards;